import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Box, Button, Flex, Text } from '@chakra-ui/react';

import AvatarProfile from 'components/user/AvatarProfile';
import AvatarStyleSelectors from 'components/user/AvatarStyleSelectors';
import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';
import StayOnPageModal from 'components/common/modals/StayOnPageModal';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';

import { updateUserProfile } from 'store/modules/user/user-action';
import { userActions } from 'store/modules/user/user-slice';
import { ReducerStatus } from 'utils/constants';
import { generateAvatar } from 'utils/avatarHelper';

/**
 * UserProfilePage - Component for user avatar customization and profile management
 * Allows users to select various avatar styles including hair, accessories, and clothing
 * Integrates with Formik for form state management and validation
 * Saves avatar configuration as JSON to the backend API
 */
function UserProfilePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state selectors
  const userProfile = useSelector((state) => state.user.userProfile);
  const updateUserProfileState = useSelector(
    (state) => state.user.updateUserProfile
  );

  // Default avatar configuration values
  const defaultAvatarValues = useMemo(
    () => ({
      topType: 'hat',
      accessories: 'default',
      accessoriesColor: '2c1b18',
      hairColor: '2c1b18',
      facialHair: 'default',
      facialHairColor: '2c1b18',
      clothing: 'shirtVNeck',
      clothingColor: 'ffffb1',
      eyes: 'default',
      eyebrows: 'default',
      mouth: 'default',
      skinColor: 'f8d25c',
    }),
    []
  );

  // Local component state
  const [initialValues, setInitialValues] = useState({
    ...defaultAvatarValues,
    gamerTag: '',
  });
  const [avatarDataUri, setAvatarDataUri] = useState('');
  const [currentFormValues, setCurrentFormValues] = useState(initialValues);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showStayOnPageModal, setShowStayOnPageModal] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);
  const [avatarCreationError, setAvatarCreationError] = useState(null);

  const userProfileValidationSchema = Yup.object({
    gamerTag: Yup.string()
      .required('Gamer tag is required')
      .min(3, 'Gamer tag must be at least 3 characters')
      .max(20, 'Gamer tag must not exceed 20 characters')
      .matches(
        /^[a-zA-Z0-9_-]+$/,
        'Gamer tag can only contain letters, numbers, underscores, and hyphens'
      ),
  });

  /**
   * Pre-populate form with existing user profile data if available
   * Parses characterJSON from API and sets initial form values
   * Falls back to default values if no profile exists
   */
  useEffect(() => {
    if (userProfile.status === ReducerStatus.SUCCEEDED && userProfile.data) {
      try {
        // Parse characterJSON if it exists
        const characterData = userProfile.data.characterJson
          ? JSON.parse(userProfile.data.characterJson)
          : {};

        // Merge character data with defaults and gamertag
        const loadedValues = {
          ...defaultAvatarValues,
          ...characterData,
          gamerTag: userProfile.data.gamerTag || '',
        };

        setInitialValues(loadedValues);
        setCurrentFormValues(loadedValues);
      } catch (error) {
        // If JSON parsing fails, use defaults
        setInitialValues({
          ...defaultAvatarValues,
          gamerTag: userProfile.data.gamertag || '',
        });
      }
    }
  }, [userProfile.status, userProfile.data, defaultAvatarValues]);

  // Generate initial avatar when initial values are set
  useEffect(() => {
    const initialAvatar = generateAvatar(initialValues);
    setAvatarDataUri(initialAvatar);
  }, [initialValues]);

  // Real-time avatar customization as user changes form values
  useEffect(() => {
    const updatedAvatar = generateAvatar(currentFormValues);
    setAvatarDataUri(updatedAvatar);
  }, [currentFormValues]);

  const handleSubmit = (values) => {
    setPendingFormValues(values);
    setShowSaveConfirmation(true);
  };

  const handleConfirmSave = () => {
    if (!pendingFormValues) return;

    try {
      const { gamerTag, ...avatarStyles } = pendingFormValues;

      // Convert avatar styles to JSON string for API
      const characterJSON = JSON.stringify(avatarStyles);

      dispatch(
        updateUserProfile({
          gamertag: gamerTag,
          characterJSON,
        })
      );
    } catch (error) {
      setAvatarCreationError(error);
    }
  };

  const handleCancelSave = () => {
    setShowSaveConfirmation(false);
    setPendingFormValues(null);
  };

  const handleSaveResult = () => {
    setShowSaveConfirmation(false);
    setPendingFormValues(null);

    dispatch(
      userActions.setUpdateUserProfle({
        status: ReducerStatus.IDLE,
        error: null,
      })
    );

    setShowStayOnPageModal(true);
  };

  const handleStayOnPage = () => {
    setShowStayOnPageModal(false);
  };

  const handleGoHome = () => {
    setShowStayOnPageModal(false);
    navigate('/');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <>
      {/* Save Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showSaveConfirmation}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        onSubmitResult={handleSaveResult}
        status={updateUserProfileState.status}
        description="Are you sure you want to save your profile changes? This will update your avatar and gamertag."
        successMessage="Your profile has been updated successfully!"
        error={updateUserProfileState.error || avatarCreationError}
      />
      {/* Stay on Page or Go Home Modal */}
      <StayOnPageModal
        isOpen={showStayOnPageModal}
        onClose={handleStayOnPage}
        onConfirm={handleGoHome}
        title="Profile Updated"
        message="Your profile has been saved successfully! Would you like to continue editing or return to the home page?"
        stayButtonText="Continue Editing"
        proceedButtonText="Go to Home"
      />
      <Flex minH="90vh" ml={{ base: 0, md: 20 }} p={10} flexDirection="column">
        <Flex>
          <Text fontSize="2xl">User Profile</Text>
        </Flex>
        {userProfile.status === ReducerStatus.LOADING ? (
          <Box height="90vh">
            <LoadingSpinner
              thickness="10px"
              size="xl"
              message="Loading user profile..."
            />
          </Box>
        ) : (
          <Box flex="1" mt={3}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={userProfileValidationSchema}
              onSubmit={handleSubmit}
            >
              {({ values }) => {
                if (
                  JSON.stringify(values) !== JSON.stringify(currentFormValues)
                ) {
                  setCurrentFormValues(values);
                }

                return (
                  <Form>
                    <Flex direction={{ base: 'column', lg: 'row' }} gap={3}>
                      {/* Avatar Profile Component - displays avatar and gamertag input */}
                      <AvatarProfile avatarDataUri={avatarDataUri} />

                      {/* Avatar Style Selectors Component - all customization options */}
                      <Box
                        boxShadow="md"
                        borderRadius="2xl"
                        padding={5}
                        w="100%"
                      >
                        <AvatarStyleSelectors />

                        {/* Action Buttons */}
                        <Flex justify="center" mt={16} gap={4}>
                          <Button width="200px" onClick={handleBackToHome}>
                            Back to Home
                          </Button>
                          <Button
                            colorScheme="red"
                            width="200px"
                            isLoading={
                              updateUserProfileState.status ===
                              ReducerStatus.LOADING
                            }
                            type="submit"
                          >
                            Save Profile
                          </Button>
                        </Flex>
                      </Box>
                    </Flex>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        )}
      </Flex>
    </>
  );
}

export default UserProfilePage;
